// IE 2026 10 25 / ODOO-348925 (заказ Игоря: тулбар AttrGraph на графе схемы — поиск, настройки, dragDrop): поиск по узлам графа — тот же механизм, что AttrGraph.graphSearch / apply_CHIPS
// (utils/searchMatch + keyboardLayout): части через пробел ищутся отдельно (все должны найтись в подписи или ключе узла), ничего — другая раскладка (EN↔RU, RU↔UK), снова ничего —
// ступени опечаток; на каждой ступени — как набрано, затем другая раскладка; нашли — дальше не усложняем. Чипсы — ИЛИ между собой, «Show rows without matches» любого чипса —
// остальное не приглушать. AttrGraph не менялся: функция повторена здесь чистой (тест — tests/unit/graphSearch.test.mjs); потребитель — AttrSchemeGraphV2
import { searchTerms, matchTerms, foundTerms, fuzzyMaxTier, FUZZY_TIERS } from '@/utils/searchMatch'
import { layoutCandidates } from '@/utils/keyboardLayout'

// entries: [[id, подпись, ...ещё тексты узла (ключ)]] → { nodes: Set(id), terms, tier, conv } — как AttrGraph.graphSearch(labels, txt) (там ищется подпись и id узла);
// matchId: false — id не ищется (у графа схемы id узла без ключа — служебный n:#путь, ключ передаётся отдельным текстом)
export function graphSearch(entries, txt, matchId = true) {
    const list = Array.isArray(entries) ? entries : []
    const t0 = searchTerms(txt)
    const tryT = (terms, tier) => {
        const s = new Set()
        for (const e of list) {
            if (!Array.isArray(e) || !e.length) continue
            for (let i = 1; i < e.length; i++) { if (e[i] !== undefined && e[i] !== null && e[i] !== '' && matchTerms(String(e[i]), terms, tier)) { s.add(e[0]); break } }
            if (matchId && !s.has(e[0]) && matchTerms(String(e[0] ?? ''), terms, tier)) s.add(e[0])
        }
        return s
    }
    if (!t0.length) return { nodes: new Set(), terms: t0, tier: 0, conv: '' }
    let s = tryT(t0, 0)
    if (s.size) return { nodes: s, terms: t0, tier: 0, conv: '' }
    let cands = []
    try { cands = layoutCandidates(String(txt ?? '')) || [] } catch (e) { cands = [] }
    for (const cand of cands) { const t = searchTerms(cand); s = tryT(t, 0); if (s.size) return { nodes: s, terms: t, tier: 0, conv: cand } }
    const maxTier = Math.max(0, ...t0.map(fuzzyMaxTier))
    for (let tier = 1; tier <= Math.min(maxTier, FUZZY_TIERS); tier++) {
        s = tryT(t0, tier); if (s.size) return { nodes: s, terms: t0, tier, conv: '' }
        for (const cand of cands) { const t = searchTerms(cand); s = tryT(t, tier); if (s.size) return { nodes: s, terms: t, tier, conv: cand } }
    }
    return { nodes: new Set(), terms: t0, tier: 0, conv: '' }
}

export const GRAPH_SEARCH_EMPTY = Object.freeze({ nodes: new Set(), list: [], terms: [], showAll: false, active: false })

// чипсы поиска графа (doc.viewParams.<граф>.CHIPS, как у AttrGraph) и набираемый текст поля (draft — до Enter) → { nodes: Set(id) — найденные любым чипсом (ИЛИ),
// list: [{ raw — объект чипса (null — набираемый текст), nodes: Set }], terms: [{ terms, tier, conv }] — для выделения совпавшей части, showAll, active — есть что искать }
// Выключенный чипс (meta.use === false — AttrChips.visibleChips его не показывает) и чипсы не поиска (CHIP-Metric …) не ищут
export function graphSearchChips(chips, entries, draft = '', matchId = true) {
    const out = { nodes: new Set(), list: [], terms: [], showAll: false, active: false }
    const one = (raw, title) => {
        const txt = String(title ?? '').trim().toLowerCase()
        if (!txt) return
        const res = graphSearch(entries, txt, matchId)
        out.active = true
        out.list.push({ raw, nodes: res.nodes })
        for (const n of res.nodes) out.nodes.add(n)
        out.terms.push({ terms: res.terms, tier: res.tier, conv: res.conv })
    }
    for (const c0 of Array.isArray(chips) ? chips : []) {
        const c = c0 && (c0._raw || c0)
        if (!c || typeof c !== 'object') continue
        if (c.meta && c.meta.use === false) continue
        if (c.segment && c.segment !== 'CHIP-Search') continue
        if (c.showAllLines) out.showAll = true
        one(c, c.title)
    }
    if (String(draft ?? '').trim()) one(null, draft)
    return out
}

// строка подписи → части [{ s, m }] (m — совпала с термином любого чипса: жирным); совпадений нет — одна часть без m
export function graphSearchParts(text, termsList) {
    const s = String(text ?? '')
    const ranges = []
    for (const x of Array.isArray(termsList) ? termsList : []) {
        if (!x || !Array.isArray(x.terms) || !x.terms.length) continue
        for (const r of foundTerms(s, x.terms, x.tier || 0)) if (r && r.end > r.start) ranges.push([Math.max(0, r.start), Math.min(s.length, r.end)])
    }
    if (!ranges.length) return [{ s, m: false }]
    ranges.sort((a, b) => a[0] - b[0])
    const merged = []
    for (const r of ranges) { const last = merged[merged.length - 1]; if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1]); else merged.push([r[0], r[1]]) }
    const parts = []
    let at = 0
    for (const [a, b] of merged) { if (a > at) parts.push({ s: s.slice(at, a), m: false }); if (b > a) parts.push({ s: s.slice(a, b), m: true }); at = b }
    if (at < s.length) parts.push({ s: s.slice(at), m: false })
    return parts
}

// следующая остановка обхода найденных узлов: pos −1 — обход не начат (› — первая, ‹ — последняя), дальше по кругу; n = 0 — −1
export function graphSearchStep(pos, dir, n) {
    const N = Math.floor(Number(n) || 0)
    if (N <= 0) return -1
    const p = Number.isInteger(pos) ? pos : -1
    const d = Number(dir) < 0 ? -1 : 1
    if (p < 0 || p >= N) return d > 0 ? 0 : N - 1
    return (p + d + N) % N
}

// IE 2026 10 25 / ODOO-348925 (заказ Игоря: тулбар AttrGraph на графе схемы — остатки пробы): D1 — один индекс обхода у Enter и ‹ › чипсов (как AttrGrid.navStep: остановка общая, у каждого чипса, которому принадлежит узел, номер — место узла
// в его подсписке по раскладке). r — graphSearchChips() ({ nodes, list }), id — текущий узел, pos — места узлов (Map | {}), keep — у чипса без этого узла оставить его номер
// (если в пределах), иначе −1 (обход не начат) → { pos — место id в общем обходе (−1 — нет), chips: [[raw, index]] }
export function graphSearchSync(r, id, pos, keep = false) {
    const out = { pos: -1, chips: [] }
    const all = r && r.nodes instanceof Set ? r.nodes : new Set()
    if (id && all.has(id)) out.pos = graphSearchOrder([...all], pos).indexOf(id)
    for (const it of (r && Array.isArray(r.list) ? r.list : [])) {
        if (!it || !it.raw) continue
        const n = it.nodes instanceof Set ? it.nodes.size : 0
        const k = id && n && it.nodes.has(id) ? graphSearchOrder([...it.nodes], pos).indexOf(id) : -1
        if (k >= 0) { out.chips.push([it.raw, k]); continue }
        const prev = it.raw.matchIndex
        out.chips.push([it.raw, keep && Number.isInteger(prev) && prev >= 0 && prev < n ? prev : -1])
    }
    return out
}

// порядок обхода: по раскладке — слева направо, затем сверху вниз (pos: Map | {} id → { x, y }); узлы без места — в конце, по порядку входа
export function graphSearchOrder(ids, pos) {
    const at = (id) => (pos instanceof Map ? pos.get(id) : pos && typeof pos === 'object' ? pos[id] : null)
    const list = [...(ids || [])]
    const idx = new Map(list.map((id, i) => [id, i]))
    return list.sort((a, b) => {
        const pa = at(a), pb = at(b)
        if (!pa || !pb) return pa ? -1 : pb ? 1 : idx.get(a) - idx.get(b)
        const dx = Math.round(pa.x) - Math.round(pb.x)
        if (Math.abs(dx) > 1) return dx
        const dy = Math.round(pa.y) - Math.round(pb.y)
        return dy || idx.get(a) - idx.get(b)
    })
}
